import { Col, Row, Text } from 'components';
import { Api } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

const RequestForm = ({ placeholder, onSubmit, onClose, loading }) => {
  const [reason, setReason] = useState('');

  return (
    <>
      <textarea
        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-[13px] text-[#1a2332] resize-none focus:outline-none focus:ring-2 focus:ring-[#635BFF]/30 focus:border-[#635BFF]"
        rows={4}
        placeholder={placeholder}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={1000}
        disabled={loading}
      />
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 rounded-lg text-[13px] font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSubmit(reason)}
          disabled={loading || !reason.trim()}
          className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-[#635BFF] hover:bg-[#5148d4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </button>
      </div>
    </>
  );
};

const RequestModal = ({ title, placeholder, onSubmit, onClose, loading }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
      <h2 className="text-[16px] font-semibold text-[#1a2332] mb-1">{title}</h2>
      <p className="text-[13px] text-[#6B7280] mb-4">
        Please provide a reason for this request. An admin will review it.
      </p>
      <RequestForm placeholder={placeholder} onSubmit={onSubmit} onClose={onClose} loading={loading} />
    </div>
  </div>
);

const PendingBadge = ({ label }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#FEF9C3] border border-[#FDE047] text-[#854D0E] text-[13px] font-medium cursor-not-allowed select-none">
    <span className="w-2 h-2 rounded-full bg-[#FBBF24] inline-block animate-pulse" />
    {label}
  </div>
);

const StripeConnect = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [modal, setModal] = useState(null); // 'replacement' | 'deletion' | null
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    fetchStatus();
    fetchPendingRequest();

    const params = new URLSearchParams(location.search);
    const connectSuccess = params.get('connectSuccess');
    const replaceSuccess = params.get('replaceSuccess');
    const connectError = params.get('connectError');

    if (connectSuccess === 'true') {
      dispatch({ type: 'HANDLE_RES', payload: { res: t('settings.stripeConnect.connectSuccess') } });
    } else if (replaceSuccess === 'true') {
      dispatch({ type: 'HANDLE_RES', payload: { res: t('settings.stripeConnect.replaceSuccess') } });
    } else if (connectError) {
      const messages = {
        invalid_team: t('settings.stripeConnect.errorInvalidTeam'),
        no_account_id: t('settings.stripeConnect.errorNoAccountId'),
        team_not_found: t('settings.stripeConnect.errorTeamNotFound'),
        oauth_failed: t('settings.stripeConnect.errorOAuthFailed'),
        invalid_replacement_token: t('settings.stripeConnect.errorInvalidReplacementToken'),
        replacement_token_expired: t('settings.stripeConnect.errorReplacementTokenExpired'),
        region_not_supported: t('settings.stripeConnect.errorRegionNotSupported')
      };
      dispatch({
        type: 'HANDLE_RES',
        payload: { res: messages[connectError] || t('settings.stripeConnect.errorGeneric') }
      });
    }
  }, []);

  async function fetchStatus() {
    setLoading(true);
    try {
      const res = await Api.get('/stripe/connect/connectStatus');
      setStatus(res?.data || { connected: false });
    } catch {
      setStatus({ connected: false });
    }
    setLoading(false);
  }

  async function fetchPendingRequest() {
    try {
      const res = await Api.get('/stripe/connect/stripeRequestStatus');
      setPendingRequest(res?.data || null);
    } catch {
      setPendingRequest(null);
    }
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const res = await Api.get('/stripe/connect/connectOAuth');
      const oauthUrl = res?.data?.oauthUrl;
      if (oauthUrl) {
        window.location.href = oauthUrl;
      } else {
        dispatch({ type: 'HANDLE_RES', payload: { res: t('settings.stripeConnect.errorNoUrl') } });
        setConnecting(false);
      }
    } catch {
      dispatch({ type: 'HANDLE_RES', payload: { res: t('settings.stripeConnect.errorInitFailed') } });
      setConnecting(false);
    }
  }

  async function handleSubmitRequest(reason) {
    if (!reason?.trim()) return;
    setSubmitting(true);
    try {
      const endpoint =
        modal === 'replacement'
          ? '/stripe/connect/requestReplacement'
          : '/stripe/connect/requestDeletion';
      const res = await Api.post(endpoint, { reason: reason.trim() });
      if (res) {
        setPendingRequest(res.data);
        setModal(null);
        dispatch({
          type: 'HANDLE_RES',
          payload: {
            res:
              modal === 'replacement'
                ? t('settings.stripeConnect.replacementRequested')
                : t('settings.stripeConnect.deletionRequested')
          }
        });
      }
    } catch {
      dispatch({ type: 'HANDLE_RES', payload: { res: t('settings.stripeConnect.errorGeneric') } });
    }
    setSubmitting(false);
  }

  return (
    <Col wid="100%">
      <Row between center marg="24px 0 16px">
        <Col>
          <Text b1 bold>
            {t('settings.stripeConnect.title')}
          </Text>
          <Text b2 marg="4px 0 0" col="#6B7280">
            {t('settings.stripeConnect.description')}
          </Text>
        </Col>
      </Row>

      {loading ? (
        <Row center marg="8px 0 0">
          <Text b2 col="#9CA3AF">
            {t('settings.stripeConnect.checkingStatus')}
          </Text>
        </Row>
      ) : status?.connected ? (
        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#16A34A] flex-shrink-0" />
            <span className="text-[13px] font-semibold text-[#15803D]">
              {t('settings.stripeConnect.connected')}
            </span>
          </div>

          <p className="font-mono text-[12px] text-[#374151]">{status.stripeAccountId}</p>

          {status.accountDetails?.email && (
            <p className="text-[13px] text-[#6B7280]">{status.accountDetails.email}</p>
          )}

          <div className="pt-2 border-t border-[#BBF7D0]">
            {pendingRequest?.type === 'replacement' ? (
              <PendingBadge label={t('settings.stripeConnect.replacementPending')} />
            ) : pendingRequest?.type === 'deletion' ? (
              <PendingBadge label={t('settings.stripeConnect.deletionPending')} />
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setModal('replacement')}
                  className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-[#635BFF] hover:bg-[#5148d4] transition-colors"
                >
                  {t('settings.stripeConnect.requestReplacement')}
                </button>
                <button
                  type="button"
                  onClick={() => setModal('deletion')}
                  className="px-4 py-2 rounded-lg text-[13px] font-medium text-[#991B1B] bg-[#FEE2E2] hover:bg-[#FECACA] transition-colors"
                >
                  {t('settings.stripeConnect.requestDeletion')}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-[#D1D5DB] flex-shrink-0" />
            <span className="text-[13px] font-semibold text-[#374151]">
              {t('settings.stripeConnect.notConnected')}
            </span>
          </div>
          <p className="text-[13px] text-[#6B7280] mb-4">{t('settings.stripeConnect.notConnectedDesc')}</p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className="px-5 py-2.5 rounded-lg text-[13px] font-semibold text-white bg-[#635BFF] hover:bg-[#5148d4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {connecting ? t('settings.stripeConnect.connecting') : t('settings.stripeConnect.connectButton')}
          </button>
        </div>
      )}

      {modal === 'replacement' && (
        <RequestModal
          title={t('settings.stripeConnect.requestReplacementTitle')}
          placeholder={t('settings.stripeConnect.requestReplacementPlaceholder')}
          onSubmit={handleSubmitRequest}
          onClose={() => setModal(null)}
          loading={submitting}
        />
      )}

      {modal === 'deletion' && (
        <RequestModal
          title={t('settings.stripeConnect.requestDeletionTitle')}
          placeholder={t('settings.stripeConnect.requestDeletionPlaceholder')}
          onSubmit={handleSubmitRequest}
          onClose={() => setModal(null)}
          loading={submitting}
        />
      )}
    </Col>
  );
};

export default StripeConnect;
